import { ErrorCode, Purchases, PurchasesError } from '@revenuecat/purchases-js'
import type { Offering, Package } from '@revenuecat/purchases-js'
import Config from '@config'
import type { PurchaseManager, PurchaseResult, PremiumOffering } from './types'

// Web purchase manager — RevenueCat Web Billing (@revenuecat/purchases-js).
//
// This is the WEB half of the purchase seam (Phase 3a). Native stays a stub
// (purchaseManager.native.ts, Phase 3b). Metro never bundles a `.web.ts` for
// native, so importing the SDK here does not affect the native app.
//
// Everything degrades gracefully: if the public key is missing the manager
// reports `isAvailable === false` and the screen keeps its "bientôt disponible"
// fallback; a missing offering or a runtime SDK error never throws out of these
// methods.

// Public RevenueCat Web Billing key (safe in the bundle — it gates nothing
// server-side). Set at build time from app config: the production web build
// ships the live key; a future dev build would ship the sandbox key.
const WEB_KEY = Config.REVENUECAT_WEB_KEY ?? ''
const IS_KEY_PRESENT = WEB_KEY.length > 0

/** The account id the SDK is currently configured/identified with. */
let currentAppUserId: string | null = null

const logWarn = (message: string, error: unknown): void => {
  // Never surface SDK internals to the user; a warning is enough for debugging.
  console.warn(`[purchases:web] ${message}`, error)
}

/**
 * Boot the SDK once with the given account id. Idempotent: if it is already
 * configured we leave it alone (use `identify` to switch users). No-op when the
 * key is missing or the account id is empty.
 */
const bootIfNeeded = (accountId: string): void => {
  if (!IS_KEY_PRESENT || !accountId) {
    return
  }
  if (Purchases.isConfigured()) {
    return
  }
  Purchases.configure({ apiKey: WEB_KEY, appUserId: accountId })
  currentAppUserId = accountId
}

/**
 * Pick the package we treat as "Premium". Prefer an explicitly configured
 * monthly/annual package, then fall back to the first available one so a
 * single-package offering still works without extra dashboard wiring.
 */
const pickPremiumPackage = (offering: Offering): Package | null =>
  offering.monthly ?? offering.annual ?? offering.availablePackages[0] ?? null

const priceLabelForPackage = (rcPackage: Package): string | null => {
  const product = rcPackage.webBillingProduct ?? rcPackage.rcBillingProduct
  return product?.price?.formattedPrice ?? product?.currentPrice?.formattedPrice ?? null
}

const fetchCurrentPremiumPackage = async (): Promise<Package | null> => {
  if (!IS_KEY_PRESENT || !Purchases.isConfigured()) {
    return null
  }
  const offerings = await Purchases.getSharedInstance().getOfferings()
  const current = offerings.current
  if (!current) {
    return null
  }
  return pickPremiumPackage(current)
}

export const purchaseManager: PurchaseManager = {
  isAvailable: IS_KEY_PRESENT,

  configure(accountId: string): Promise<void> {
    try {
      bootIfNeeded(accountId)
    } catch (error) {
      logWarn('configure failed', error)
    }
    return Promise.resolve()
  },

  async identify(accountId: string): Promise<void> {
    if (!IS_KEY_PRESENT || !accountId) {
      return
    }
    try {
      if (!Purchases.isConfigured()) {
        bootIfNeeded(accountId)
        return
      }
      if (currentAppUserId !== accountId) {
        await Purchases.getSharedInstance().changeUser(accountId)
        currentAppUserId = accountId
      }
    } catch (error) {
      logWarn('identify failed', error)
    }
  },

  logOut(): Promise<void> {
    if (!IS_KEY_PRESENT) {
      return Promise.resolve()
    }
    try {
      if (Purchases.isConfigured()) {
        Purchases.getSharedInstance().close()
      }
    } catch (error) {
      logWarn('logOut failed', error)
    } finally {
      currentAppUserId = null
    }
    return Promise.resolve()
  },

  async getOffering(): Promise<PremiumOffering | null> {
    try {
      const rcPackage = await fetchCurrentPremiumPackage()
      if (!rcPackage) {
        return null
      }
      return { priceLabel: priceLabelForPackage(rcPackage) }
    } catch (error) {
      logWarn('getOffering failed', error)
      return null
    }
  },

  async purchasePremium(): Promise<PurchaseResult> {
    if (!IS_KEY_PRESENT || !Purchases.isConfigured()) {
      return { ok: false, reason: 'error' }
    }
    try {
      const rcPackage = await fetchCurrentPremiumPackage()
      if (!rcPackage) {
        return { ok: false, reason: 'error' }
      }
      await Purchases.getSharedInstance().purchase({ rcPackage })
      return { ok: true }
    } catch (error) {
      if (error instanceof PurchasesError && error.errorCode === ErrorCode.UserCancelledError) {
        return { ok: false, reason: 'cancelled' }
      }
      logWarn('purchasePremium failed', error)
      return { ok: false, reason: 'error' }
    }
  },

  // Web entitlements are granted server-side (RevenueCat webhook → account id),
  // so there is nothing to restore on the client. Resolve ok; the caller
  // refetches the subscription status to reflect the server-authoritative state.
  restorePurchases(): Promise<PurchaseResult> {
    return Promise.resolve({ ok: true })
  },

  // RevenueCat hands out the page where the customer stops their subscription:
  // its hosted Web Billing page for a web purchase, the store page otherwise.
  async getManagementUrl(): Promise<string | null> {
    if (!IS_KEY_PRESENT || !Purchases.isConfigured()) {
      return null
    }
    try {
      const customerInfo = await Purchases.getSharedInstance().getCustomerInfo()
      return customerInfo.managementURL ?? null
    } catch (error) {
      logWarn('getManagementUrl failed', error)
      return null
    }
  },

  // Renewal is only known to RevenueCat: the server keeps the same end date
  // whether the subscription renews or was stopped.
  async getWillRenew(): Promise<boolean | null> {
    if (!IS_KEY_PRESENT || !Purchases.isConfigured()) {
      return null
    }
    try {
      const customerInfo = await Purchases.getSharedInstance().getCustomerInfo()
      const active = Object.values(customerInfo.entitlements?.active ?? {})
      return active.length ? active.every((entitlement) => entitlement.willRenew) : null
    } catch (error) {
      logWarn('getWillRenew failed', error)
      return null
    }
  },
}

export type { PurchaseManager, PurchaseResult } from './types'
